class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, this.tagName === 'CART-DRAWER-ITEMS' ? 80 : ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', (event) => {
      // Quantity buttons dispatch a change immediately; debounce only typing.
      if (this.tagName === 'CART-DRAWER-ITEMS' && !event.isTrusted) {
        this.onChange(event);
      } else {
        debouncedOnChange(event);
      }
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      if (this.tagName === 'CART-DRAWER-ITEMS' && ['product-form', 'jk-upsell'].includes(event.source)) return;
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}, #Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    // Zero removes a cart line; minimum purchase rules only apply to retained items.
    if (inputValue === 0) {
      event.target.setCustomValidity('');
      return this.updateQuantity(index, 0, event, undefined, event.target.dataset.quantityVariantId);
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const quantityInput = this.querySelector(`#Drawer-quantity-${line}, #Quantity-${line}`);
    const previousQuantity = quantityInput?.getAttribute('value');
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    this.enableLoading(line);
    const removedRow = quantity === 0 ? this.querySelector(`#CartDrawer-Item-${line}`) : null;
    const restoreOffer = removedRow
      ? this.querySelector('jk-cart-offers')?.previewRemoval?.(Number(previousQuantity) || 0)
      : null;
    let removalAnimation;
    const restoreRemovedRow = () => {
      restoreOffer?.();
      removalAnimation?.cancel();
      if (removedRow) {
        removedRow.hidden = false;
        removedRow.classList.remove('jk-cart-item-removing');
      }
    };
    if (removedRow) {
      if (removedRow.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const height = removedRow.getBoundingClientRect().height;
        const styles = getComputedStyle(removedRow);
        removedRow.classList.add('jk-cart-item-removing');
        removalAnimation = removedRow.animate([
          { height: `${height}px`, opacity: 1, transform: 'translateX(0)', paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom },
          { height: `${height}px`, opacity: 0, transform: 'translateX(12px)', paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom, offset: 0.45 },
          { height: '0px', opacity: 0, transform: 'translateX(12px)', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', borderBottomWidth: '0px' },
        ], { duration: 380, easing: 'ease-out', fill: 'forwards' });
        removalAnimation.finished.then(() => { removedRow.hidden = true; }).catch(() => {});
      } else {
        removedRow.hidden = true;
      }
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: routes.cart_url,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    const changeUrl = routes.cart_change_url.replace(/(?:\.js)?$/, '.js');
    return fetch(changeUrl, { ...fetchConfig(), ...{ body } })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok && !state.errors) throw new Error(state.description || `Cart update failed (${response.status})`);
        return state;
      })
      .then(async (parsedState) => {
        // Shopify can accept the quantity change but return a null section.
        // Fetch fresh markup in that case; never repeat the cart mutation.
        const sections = this.getSectionsToRender();
        const prepareSections = (markup) => sections.map((section) => {
          const container = document.getElementById(section.id);
          if (!container) throw new Error(`Cart target missing: ${section.id}`);
          return {
            target: container.querySelector(section.selector) || container,
            html: this.getSectionInnerHTML(markup?.[section.section], section.selector),
          };
        });
        let updates;
        if (!parsedState.errors) {
          try {
            updates = prepareSections(parsedState.sections);
          } catch (sectionError) {
            const drawer = document.querySelector('cart-drawer');
            if (this.tagName === 'CART-DRAWER-ITEMS' && drawer?.recoverSections) {
              // Skip a second section request that can return the same null markup.
              updates = prepareSections(await drawer.recoverSections());
            } else {
              const url = new URL(window.location.pathname, window.location.origin);
              url.searchParams.set('sections', sections.map((section) => section.section).join(','));
              const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
              if (!response.ok) throw sectionError;
              updates = prepareSections(await response.json());
            }
          }
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            restoreRemovedRow();
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          // Validate every section before replacing any of them.
          updates.forEach(({ target, html }) => {
            target.innerHTML = html;
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          // These are post-render UI enhancements. Their failure must not report
          // an already saved quantity change as a failed cart mutation.
          try {
            this.updateLiveRegions(line, message);

            const lineItem =
              document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }
          } catch (error) {
            console.error('Cart accessibility refresh failed:', error);
          }
        });

        try {
          CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);
        } catch (error) {
          console.error('Cart performance measurement failed:', error);
        }

        // A product-section/app subscriber can throw synchronously. Keep that
        // separate from the cart request and handle rejected subscribers too.
        Promise.resolve()
          .then(() => publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId }))
          .catch((error) => console.error('Cart subscriber refresh failed:', error));
      })
      .catch((error) => {
        if (removedRow?.isConnected) restoreRemovedRow();
        console.error('Cart quantity update failed:', error);
        if (quantityInput?.isConnected && previousQuantity !== null) quantityInput.value = previousQuantity;
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    const lineItemStatus = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    lineItemStatus?.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus?.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus?.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (typeof html !== 'string') throw new Error(`Cart section unavailable: ${selector}`);
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!section) throw new Error(`Cart section selector missing: ${selector}`);
    return section.innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    const lineItemStatus = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    lineItemStatus?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
